import React, { useEffect, useRef, useCallback } from 'react';

/**
 * withKinematicSensors
 * Higher-Order Component (HOC) that standardizes the capture of kinematic telemetry 
 * (TTFI, cursor volatility, tab switching, and hover durations) across all evaluation modules.
 * Isolates high-frequency DOM listeners via refs to prevent render cascades.
 */
export const withKinematicSensors = (WrappedComponent) => {
  return function KinematicWrapper(props) {
    const mountTime = useRef(Date.now());
    const firstInteractionMs = useRef(null);
    const jiggleCount = useRef(0);
    const tabSwitches = useRef(0);
    const lastMousePos = useRef({ x: null, y: null });
    
    const hoverStartTimes = useRef({});
    const hoverDurations = useRef({});

    useEffect(() => {
      mountTime.current = Date.now();
      
      const handleMouseMove = (e) => {
        // Disengage if synthetic bot execution is active
        if (window.__PUPPETEER_ACTIVE__) return;

        if (lastMousePos.current.x !== null) {
          const dx = Math.abs(e.clientX - lastMousePos.current.x);
          const dy = Math.abs(e.clientY - lastMousePos.current.y);
          // Volatility Threshold
          if (dx > 45 || dy > 45) {
            jiggleCount.current += 1;
          }
        }
        lastMousePos.current = { x: e.clientX, y: e.clientY };
      };

      const handleFirstInteraction = () => {
        if (firstInteractionMs.current === null && !window.__PUPPETEER_ACTIVE__) {
          firstInteractionMs.current = Date.now() - mountTime.current;
        }
      };

      const handleVisibilityChange = () => {
        if (document.hidden) {
          tabSwitches.current += 1;
        }
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('click', handleFirstInteraction, true);
      window.addEventListener('keydown', handleFirstInteraction, true);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('click', handleFirstInteraction, true);
        window.removeEventListener('keydown', handleFirstInteraction, true);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }, []);

    const resetKinematics = useCallback(() => {
      mountTime.current = Date.now();
      firstInteractionMs.current = null;
      jiggleCount.current = 0;
      tabSwitches.current = 0;
      lastMousePos.current = { x: null, y: null };
      hoverStartTimes.current = {};
      hoverDurations.current = {};
    }, []);

    const getKinematicMetrics = useCallback(() => ({
      ttfi: firstInteractionMs.current,
      jiggleCount: jiggleCount.current,
      tabSwitches: tabSwitches.current,
      hoverDurationsMs: Object.keys(hoverDurations.current).length > 0 ? { ...hoverDurations.current } : undefined
    }), []);

    const startHover = useCallback((id) => {
      hoverStartTimes.current[id] = Date.now();
    }, []);

    const endHover = useCallback((id) => {
      if (hoverStartTimes.current[id]) {
        const duration = Date.now() - hoverStartTimes.current[id];
        hoverDurations.current[id] = (hoverDurations.current[id] || 0) + duration;
        delete hoverStartTimes.current[id];
      }
    }, []);

    // Intercepts the component's standard onComplete, injects the metrics into the payload, and fires.
    const handleComplete = useCallback((payload) => {
      if (props.onComplete) {
        const metrics = getKinematicMetrics();
        let enrichedPayload = { ...payload };

        if (enrichedPayload.rawInteractions && Array.isArray(enrichedPayload.rawInteractions)) {
            const lastIdx = enrichedPayload.rawInteractions.length - 1;
            if (lastIdx >= 0) {
                enrichedPayload.rawInteractions[lastIdx] = {
                    ...enrichedPayload.rawInteractions[lastIdx],
                    ...metrics
                };
            }
        } else {
            enrichedPayload = { ...enrichedPayload, ...metrics };
        }

        props.onComplete(enrichedPayload);
      }
    }, [props.onComplete, getKinematicMetrics]);

    return (
      <WrappedComponent 
        {...props} 
        onComplete={props.onComplete ? handleComplete : undefined}
        resetKinematics={resetKinematics}
        getKinematicMetrics={getKinematicMetrics}
        startHover={startHover}
        endHover={endHover}
      />
    );
  };
};